import { defineStore } from "pinia";
import api from "@/plugins/api";
import {
  Project,
  ConsumptionData,
  ProjectSummary,
  ProjectDevice,
} from "@/types/projectTypes";
import { useMapStateStore } from "@/stores/mapState";

export const useProjectSummaryStore = defineStore("projectSummary", {
  state: () => ({
    projectSummaries: [] as ProjectSummary[],
    isLoading: false,
    error: null as string | null,
  }),

  getters: {
    getProjectSummaries: (state) => state.projectSummaries,
    getProjectById: (state) => (id: number) =>
      state.projectSummaries.find((project) => project.id === id),
  },

  actions: {
    async fetchProjectSummaries() {
      try {
        this.isLoading = true;
        this.error = null;

        const response = await api.get<ProjectSummary[]>("/projects/summary/");
        this.projectSummaries = response.data;

        // Store in localStorage
        localStorage.setItem("projectSummaries", JSON.stringify(response.data));

        return response.data;
      } catch (error) {
        console.error("Failed to fetch project summaries:", error);
        this.error = "Failed to load project summaries";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    initializeFromStorage() {
      const storedSummaries = localStorage.getItem("projectSummaries");
      if (storedSummaries) {
        try {
          this.projectSummaries = JSON.parse(storedSummaries);
        } catch (error) {
          console.error("Failed to parse stored project summaries:", error);
          localStorage.removeItem("projectSummaries");
        }
      }
    },

    clearProjectSummaries() {
      this.projectSummaries = [];
      localStorage.removeItem("projectSummaries");
    },
  },
});

export const useProjectStore = defineStore("project", {
  state: () => ({
    project: null as Project | null,
  }),

  getters: {
    getCurrentProject: (state) => state.project,
  },

  actions: {
    async fetchProject(projectId: number) {
      try {
        console.log("Fetching project with ID:", projectId);
        const response = await api.get<Project>(`/projects/${projectId}/`);
        console.log("API Response:", response.data);
        if (response.data) {
          this.$patch({ project: response.data });
          localStorage.setItem("project", JSON.stringify(response.data));
          console.log("Project stored in state:", this.project);

          // Update mapStateStore with project's map state
          const mapStateStore = useMapStateStore();
          if (response.data.map_state) {
            const { center, zoom, screenshot_info } = response.data.map_state;
            if (center && zoom) {
              mapStateStore.updateMapState(center, zoom);
            }
            if (screenshot_info) {
              mapStateStore.setScreenshotInfo(screenshot_info);
            }
            console.log(
              "Map state updated from project:",
              response.data.map_state
            );
          }
        }
        return response.data;
      } catch (error) {
        console.error("Failed to fetch project:", error);
        throw error;
      }
    },

    async saveProject(projectData: Project) {
      try {
        console.log(
          "[saveProject] Starting save process with data:",
          projectData
        );
        console.log("[saveProject] Project ID:", projectData.id);

        // Log project data to console
        console.log(
          "[saveProject] Project data:",
          JSON.stringify(projectData, null, 2)
        );

        // Calculate sizes of different components
        const calculateSize = (data: any): number => {
          const jsonString = JSON.stringify(data);
          return new Blob([jsonString]).size;
        };

        // Calculate total request size
        const totalSize = calculateSize(projectData);

        // Calculate sizes of heavy components
        const mapScreenshotSize = projectData.map_state?.screenshot_info
          ?.imageUrl
          ? calculateSize(projectData.map_state.screenshot_info.imageUrl)
          : 0;

        const drawingScreenshotSize = projectData.drawing_data
          ?.drawing_screenshot
          ? calculateSize(projectData.drawing_data.drawing_screenshot)
          : 0;

        // Calculate size without heavy components
        const projectWithoutScreenshots = {
          ...projectData,
          map_state: projectData.map_state
            ? {
                ...projectData.map_state,
                screenshotInfo: projectData.map_state.screenshot_info
                  ? {
                      ...projectData.map_state.screenshot_info,
                      imageUrl: null,
                    }
                  : null,
              }
            : null,
          drawing_data: projectData.drawing_data
            ? {
                ...projectData.drawing_data,
                drawingScreenshot: null,
              }
            : null,
        };
        const sizeWithoutScreenshots = calculateSize(projectWithoutScreenshots);

        // Log detailed size information
        console.log(`[saveProject] Size Analysis:`);
        console.log(
          `- Total request size: ${totalSize} bytes (${(
            totalSize / 1024
          ).toFixed(2)} KB)`
        );
        console.log(
          `- Map screenshot size: ${mapScreenshotSize} bytes (${(
            mapScreenshotSize / 1024
          ).toFixed(2)} KB)`
        );
        console.log(
          `- Drawing screenshot size: ${drawingScreenshotSize} bytes (${(
            drawingScreenshotSize / 1024
          ).toFixed(2)} KB)`
        );
        console.log(
          `- Size without screenshots: ${sizeWithoutScreenshots} bytes (${(
            sizeWithoutScreenshots / 1024
          ).toFixed(2)} KB)`
        );
        console.log(
          `- Screenshots percentage: ${(
            ((mapScreenshotSize + drawingScreenshotSize) / totalSize) *
            100
          ).toFixed(2)}% of total size`
        );

        if (projectData.id && projectData.id !== 0) {
          console.log(
            "[saveProject] Updating existing project with ID:",
            projectData.id
          );
          const response = await api.patch(
            `/projects/${projectData.id}/`,
            projectData
          );
          console.log("[saveProject] Update response:", response.data);

          const updatedProject = {
            ...projectData,
            ...response.data,
          };
          console.log(
            "[saveProject] Merged updated project data:",
            updatedProject
          );

          this.$patch({ project: updatedProject });
          localStorage.setItem("project", JSON.stringify(updatedProject));
          console.log(
            "[saveProject] Project updated successfully in store and localStorage"
          );
          return response.data;
        } else {
          console.log("[saveProject] Creating new project");
          const response = await api.post("/projects/", projectData);
          console.log("[saveProject] Create response:", response.data);

          this.$patch({ project: response.data });
          localStorage.setItem("project", JSON.stringify(response.data));
          console.log(
            "[saveProject] New project created and stored successfully"
          );
          return response.data;
        }
      } catch (error) {
        console.error("[saveProject] Failed to save project:", error);
        throw error;
      }
    },

    async deleteProject(projectId: number) {
      try {
        await api.delete(`/projects/${projectId}/`);
        if (this.project?.id === projectId) {
          this.$patch({ project: null });
          localStorage.removeItem("project");
        }
      } catch (error) {
        console.error("Failed to delete project:", error);
        throw error;
      }
    },

    clearProject() {
      this.$patch({ project: null });
      localStorage.removeItem("project");
    },

    initializeFromStorage() {
      const storedProject = localStorage.getItem("project");
      if (storedProject) {
        try {
          const parsedProject = JSON.parse(storedProject);
          this.$patch({ project: parsedProject });
        } catch (error) {
          console.error("Failed to parse stored project:", error);
          localStorage.removeItem("project");
        }
      }
    },

    setConsumptionData(consumption_data: ConsumptionData) {
      console.log(
        "[setConsumptionData] Setting consumption data:",
        consumption_data
      );
      if (this.project) {
        const updatedProject = {
          ...this.project,
          consumption_data: consumption_data,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      }
      // Show full project data structure
      console.log(
        "[setConsumptionData] Full project data structure:",
        JSON.stringify(this.project, null, 2)
      );
      // Show project keys
      console.log(
        "[setConsumptionData] Project keys:",
        this.project ? Object.keys(this.project) : "No project"
      );
      // Show consumption data specifically
      console.log(
        "[setConsumptionData] Consumption data:",
        this.project?.consumption_data
      );
    },

    setMapState(map_state: Project["map_state"]) {
      if (this.project) {
        const updatedProject = {
          ...this.project,
          map_state: map_state,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      }
    },

    setDrawingData(drawing_data: Project["drawing_data"]) {
      if (this.project) {
        const updatedProject = {
          ...this.project,
          drawing_data: drawing_data,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      }
    },

    clearDrawingData() {
      if (this.project) {
        const updatedProject = {
          ...this.project,
          drawing_data: {
            polygons: [],
            obstacles: [],
            panels: [],
            viewportTransform: { x: 0, y: 0, zoom: 1 },
            canvasRotation: 0,
            zoom: 1,
            total_usable_area: 0,
            total_obstacle_area: 0,
            total_panels: 0,
          },
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      }
    },

    setDevices(devices: Project["devices"]) {
      if (this.project) {
        const updatedProject = {
          ...this.project,
          devices,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      }
    },

    setProjectDevices(devices: ProjectDevice[]) {
      if (this.project) {
        console.log(
          "[setProjectDevices] Updating project devices with:",
          devices
        );

        // Log device types and counts for better visibility
        const deviceCounts = devices.reduce((counts, device) => {
          counts[device.device_type] = (counts[device.device_type] || 0) + 1;
          return counts;
        }, {} as Record<string, number>);

        console.log("[setProjectDevices] Device type summary:", deviceCounts);

        // Log detailed information for each device
        console.log("[setProjectDevices] Detailed device information:");
        devices.forEach((device) => {
          console.log(
            `  - Type: ${device.device_type}, ID: ${device.device_id}, Quantity: ${device.pcs}`
          );
        });

        const updatedProject = {
          ...this.project,
          devices: devices,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
        console.log("[setProjectDevices] Project devices updated successfully");
      } else {
        console.warn(
          "[setProjectDevices] Cannot update devices: No active project"
        );
      }
    },

    setSelectedKit(kitId: number | string) {
      if (this.project) {
        console.log("[setSelectedKit] Setting selected kit ID:", kitId);
        const updatedProject = {
          ...this.project,
          selected_kit_id: kitId,
        };
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
        console.log("[setSelectedKit] Kit selection updated successfully");
      } else {
        console.warn("[setSelectedKit] Cannot update kit selection: No active project");
      }
    },

    setPhaseType(phase: "single" | "three") {
      if (this.project) {
        console.log("[setPhaseType] Setting project phase_type:", phase);
        const updatedProject = {
          ...this.project,
          phase_type: phase,
        } as Project;
        this.$patch({ project: updatedProject });
        localStorage.setItem("project", JSON.stringify(updatedProject));
      } else {
        console.warn("[setPhaseType] Cannot update phase_type: No active project");
      }
    },

    dataValidatorBeforeSave(projectData: Project): {
      isValid: boolean;
      message?: string;
    } {
      try {
        console.log(
          "[dataValidatorBeforeSave] Starting validation for project:",
          projectData.project_name
        );
        console.log("[dataValidatorBeforeSave] Project ID:", projectData.id);

        // 1. Validate Project Basic Info
        console.log(
          "[dataValidatorBeforeSave] Validating project basic info..."
        );
        if (
          !projectData.project_name ||
          projectData.project_name.trim().length === 0
        ) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Project name is required"
          );
          return { isValid: false, message: "Project name is required" };
        }

        if (projectData.project_name.length > 200) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Project name too long"
          );
          return {
            isValid: false,
            message: "Project name cannot exceed 200 characters",
          };
        }

        // 2. Validate Status and Service Type
        console.log(
          "[dataValidatorBeforeSave] Validating status and service type..."
        );
        const validStatuses = ["draft", "in_progress", "completed"];
        if (!validStatuses.includes(projectData.status)) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid status"
          );
          return { isValid: false, message: "Invalid project status" };
        }

        const validServiceTypes = [
          "product_supply",
          "product_supply_commissioning",
          "full_service",
          "complete_solution",
        ];
        if (!validServiceTypes.includes(projectData.service_type)) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid service type"
          );
          return { isValid: false, message: "Invalid service type" };
        }

        // 3. Validate Client Information
        console.log(
          "[dataValidatorBeforeSave] Validating client information..."
        );
        if (
          projectData.client_email &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(projectData.client_email)
        ) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid email format"
          );
          return { isValid: false, message: "Invalid client email format" };
        }

        const validClientTypes = ["standard", "installer", "distributor"];
        if (!validClientTypes.includes(projectData.client_type)) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid client type"
          );
          return { isValid: false, message: "Invalid client type" };
        }

        // 4. Validate Location Information
        console.log(
          "[dataValidatorBeforeSave] Validating location information..."
        );
        if (
          projectData.latitude &&
          (projectData.latitude < -90 || projectData.latitude > 90)
        ) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid latitude"
          );
          return { isValid: false, message: "Invalid latitude value" };
        }

        if (
          projectData.longitude &&
          (projectData.longitude < -180 || projectData.longitude > 180)
        ) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Invalid longitude"
          );
          return { isValid: false, message: "Invalid longitude value" };
        }

        // 5. Validate Project Specifications
        console.log(
          "[dataValidatorBeforeSave] Validating project specifications..."
        );
        if (projectData.usable_area && projectData.usable_area < 0) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Negative usable area"
          );
          return { isValid: false, message: "Usable area cannot be negative" };
        }

        if (projectData.total_power && projectData.total_power < 0) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Negative total power"
          );
          return { isValid: false, message: "Total power cannot be negative" };
        }

        // 6. Validate Devices
        console.log("[dataValidatorBeforeSave] Validating devices...");
        if (projectData.devices) {
          const validDeviceTypes = [
            "Inverter",
            "Panel",
            "Battery",
            "EVCharger",
            "Meter",
            "Kit",
          ];

          for (const device of projectData.devices) {
            if (!validDeviceTypes.includes(device.device_type)) {
              console.log(
                "[dataValidatorBeforeSave] Invalid device data:",
                device
              );
              return {
                isValid: false,
                message: `Invalid device type: ${device.device_type}`,
              };
            }

            if (!device.device_id || device.device_id <= 0) {
              console.log(
                "[dataValidatorBeforeSave] Validation failed: Invalid device ID"
              );
              return {
                isValid: false,
                message: "Device ID must be a positive number",
              };
            }

            if (!device.pcs || device.pcs <= 0) {
              console.log(
                "[dataValidatorBeforeSave] Validation failed: Invalid device quantity"
              );
              return {
                isValid: false,
                message: "Number of pieces must be a positive number",
              };
            }
          }
        }

        // 7. Validate Panel Count - Make sure drawn panels match selected panels
        console.log("[dataValidatorBeforeSave] Validating panel count...");

        // Count panels from devices
        const panelDevices =
          projectData.devices?.filter(
            (device) => device.device_type === "Panel"
          ) || [];

        // Calculate total number of panel devices
        const devicePanelCount = panelDevices.reduce(
          (sum, device) => sum + (device.pcs || 0),
          0
        );

        // Count panels in drawing_data
        const drawnPanelCount = projectData.drawing_data?.panels?.length || 0;

        console.log("[dataValidatorBeforeSave] Panel count validation:", {
          devicePanelCount,
          drawnPanelCount,
        });

        // Compare counts - only validate if there are panels in the devices
        if (devicePanelCount > 0 && devicePanelCount !== drawnPanelCount) {
          console.log(
            "[dataValidatorBeforeSave] Validation failed: Panel count mismatch"
          );
          return {
            isValid: false,
            message: `Panel count mismatch: ${devicePanelCount} panels selected in configuration but ${drawnPanelCount} panels placed on drawing. Please place all panels on the drawing.`,
          };
        }

        // 8. Validate Map State
        console.log("[dataValidatorBeforeSave] Validating map state...");
        if (projectData.map_state) {
          if (
            !projectData.map_state.center ||
            typeof projectData.map_state.center.lat !== "number" ||
            typeof projectData.map_state.center.lng !== "number"
          ) {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Invalid map center"
            );
            return {
              isValid: false,
              message: "Invalid map center coordinates",
            };
          }

          if (typeof projectData.map_state.zoom !== "number") {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Invalid map zoom"
            );
            return { isValid: false, message: "Invalid map zoom level" };
          }
        }

        // 9. Validate Drawing Data
        console.log("[dataValidatorBeforeSave] Validating drawing data...");
        if (projectData.drawing_data) {
          if (
            projectData.drawing_data.total_usable_area &&
            projectData.drawing_data.total_usable_area < 0
          ) {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Negative usable area"
            );
            return {
              isValid: false,
              message: "Total usable area cannot be negative",
            };
          }

          if (
            projectData.drawing_data.total_obstacle_area &&
            projectData.drawing_data.total_obstacle_area < 0
          ) {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Negative obstacle area"
            );
            return {
              isValid: false,
              message: "Total obstacle area cannot be negative",
            };
          }

          if (
            projectData.drawing_data.total_panels &&
            projectData.drawing_data.total_panels < 0
          ) {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Negative panel count"
            );
            return {
              isValid: false,
              message: "Total panels cannot be negative",
            };
          }
        }

        // 10. Validate Consumption Data
        console.log("[dataValidatorBeforeSave] Validating consumption data...");
        if (projectData.consumption_data) {
          const validateData = (data: number[] | null, isYearly: boolean) => {
            if (!data || !Array.isArray(data)) return false;
            if (isYearly) {
              // For yearly data, expect exactly one value
              return data.length === 1;
            } else {
              // For monthly data, expect exactly 12 values
              return data.length === 12;
            }
          };

          const isYearly = projectData.consumption_data.is_yearly_data;
          if (
            !validateData(projectData.consumption_data.f1_data, isYearly) ||
            !validateData(projectData.consumption_data.f2_data, isYearly) ||
            !validateData(projectData.consumption_data.f3_data, isYearly)
          ) {
            console.log(
              "[dataValidatorBeforeSave] Validation failed: Invalid consumption data"
            );
            return {
              isValid: false,
              message: isYearly
                ? "Yearly consumption data must contain exactly one value per phase"
                : "Monthly consumption data must contain 12 values per phase",
            };
          }
        }

        console.log(
          "[dataValidatorBeforeSave] All validations passed successfully"
        );
        return { isValid: true };
      } catch (error) {
        console.error("[dataValidatorBeforeSave] Validation error:", error);
        return {
          isValid: false,
          message: "An unexpected error occurred during validation",
        };
      }
    },
  },
});
